/**
 * Composer.SlashCommandMenu — quick-action palette anchored to the composer.
 *
 * v1 had no first-class slash menu; this is the v2 SOTA addition (May 2026)
 * that surfaces deterministic chat utilities (`/help`, `/clear`, `/sources`,
 * `/export`) without yet another popover trigger — it appears automatically
 * when the textarea content starts with `/`.
 *
 * The component is **headless**: it takes a filtered command list (the
 * parent runs the prefix-match against the user's draft) and renders the
 * floating panel. Selection invokes `onSelect(commandId)` — the parent
 * decides whether to clear the textarea, run an action, etc.
 *
 * Accessibility:
 *   - `role="listbox"` with `aria-activedescendant`
 *   - Up/Down keyboard navigation (handled inline)
 *   - Escape closes
 *
 * The component renders **inline** above the textarea so screen readers
 * announce it in document order. We avoid Radix Popover here because the
 * trigger is the textarea itself, not a button — the open state is
 * derived from the textarea content (`value.startsWith("/")`).
 */

import * as React from "react";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";

export interface SlashCommand {
  /** Stable identifier (e.g. `"help"`, `"clear"`). */
  id: string;
  /** Visible command (e.g. `"/help"`). */
  command: string;
  /** Short description (Italian copy). */
  description: string;
  /** Optional lucide icon. */
  icon?: React.ComponentType<{ size?: number; className?: string }>;
}

export interface SlashCommandMenuProps {
  /** Pre-filtered list. Empty array → renders an "Nessun comando" footer. */
  commands: SlashCommand[];
  /** Called when the user picks a command (click or Enter). */
  onSelect: (commandId: string) => void;
  /** Optional: close request from Escape / blur. */
  onDismiss?: () => void;
  className?: string;
}

export const SlashCommandMenu = React.forwardRef<HTMLDivElement, SlashCommandMenuProps>(
  ({ commands, onSelect, onDismiss, className }, ref) => {
    const { i18n } = useI18n();
    const [activeIndex, setActiveIndex] = React.useState(0);

    // Reset cursor when the filtered list changes. Tracking a stable
    // ID projection inside `useMemo` prevents thrash when the parent
    // passes a freshly-mapped `commands` array on every render: the
    // memoised `idSignature` stays equal across renders unless the
    // underlying command IDs change.
    const idSignature = React.useMemo(
      () => commands.map((c) => c.id).join("|"),
      [commands],
    );
    React.useEffect(() => {
      // Read `idSignature` so Biome's exhaustive-deps treats it as a real
      // dependency (the reset *depends on* a change in the signature).
      void idSignature;
      setActiveIndex(0);
    }, [idSignature]);

    // Keyboard nav lives on a document listener so the textarea (which keeps
    // focus) doesn't have to forward arrow keys manually. We only bind while
    // commands are present.
    React.useEffect(() => {
      if (commands.length === 0) return;
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setActiveIndex((i) => Math.min(i + 1, commands.length - 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
          const cmd = commands[activeIndex];
          if (cmd) {
            e.preventDefault();
            onSelect(cmd.id);
          }
        } else if (e.key === "Escape") {
          e.preventDefault();
          onDismiss?.();
        }
      };
      window.addEventListener("keydown", onKey, { capture: true });
      return () =>
        window.removeEventListener("keydown", onKey, { capture: true });
    }, [activeIndex, commands, onDismiss, onSelect]);

    return (
      <div
        ref={ref}
        aria-label={i18n._(msg`Comandi rapidi`)}
        className={cn(
          "rounded-lg border border-hairline bg-white/95 shadow-[0_4px_16px_-6px_rgba(0,0,0,0.10)]",
          "p-1 backdrop-blur-md",
          "animate-chip-in",
          className,
        )}
      >
        {commands.length === 0 ? (
          <div className="px-2 py-1.5 text-[11px] text-ink-3">
            <Trans>Nessun comando corrisponde.</Trans>
          </div>
        ) : (
          <div role="menu">
            {commands.map((c, i) => {
              const Icon = c.icon;
              const active = i === activeIndex;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="menuitem"
                  aria-current={active ? "true" : undefined}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => onSelect(c.id)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
                    "transition-colors duration-150 ease-out",
                    active
                      ? "bg-ando-orange/10 text-ink-1"
                      : "text-ink-2 hover:bg-ink-1/[0.04]",
                  )}
                >
                  {Icon ? (
                    <Icon
                      size={13}
                      className={cn(
                        "shrink-0",
                        active ? "text-ando-orange" : "text-ink-3",
                      )}
                    />
                  ) : null}
                  <span className="font-medium font-mono text-[11.5px] text-ink-1">
                    {c.command}
                  </span>
                  <span className="truncate text-[10.5px] text-ink-3">
                    {c.description}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);
SlashCommandMenu.displayName = "Composer.SlashCommandMenu";
